import { useCallback, useEffect, useState } from 'react'
import { useEventChannel } from '@/hooks/useEventChannel'
import { api } from '@/util/api'
import { browserTimeZone, type RegistrationSummary } from './preShow'

const path = encodeURIComponent

/** The pre-show's registration endpoints (docs/API_SPECIFICATION.md, Pre-show reminders). Each answers the summary. */
export const registrationsApi = {
  summary: (eventId: string) => api.get<{ data: RegistrationSummary }>(`/public/events/${path(eventId)}/registrations`),
  register: (eventId: string, timeZone: string | null) =>
    api.post<{ data: RegistrationSummary }>(`/events/${path(eventId)}/registration`, timeZone ? { time_zone: timeZone } : {}),
  unregister: (eventId: string) => api.delete<{ data: RegistrationSummary }>(`/events/${path(eventId)}/registration`),
}

export interface Registrations {
  /** Null until it loads (and when it can't). */
  summary: RegistrationSummary | null
  /** Registers the signed-in viewer with the browser's time zone. Rejects when the API refuses. */
  register(): Promise<void>
  /** Takes the viewer's registration back. Rejects when the API refuses. */
  unregister(): Promise<void>
}

/**
 * The event's registrations while the pre-show is on screen (`enabled`): loaded for the signed-in viewer (`token`
 * changing loads again, so signing in learns whether they registered), then kept current over the event's public
 * channel (`registrations.updated`), which carries everyone's count and places but not the viewer's own `registered`.
 */
export function useRegistrations(eventId: string, { enabled, token }: { enabled: boolean; token: string | null }): Registrations {
  const [summary, setSummary] = useState<RegistrationSummary | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    registrationsApi.summary(eventId).then(
      (res) => {
        if (!cancelled) setSummary(res.data)
      },
      () => {
        // The pre-show works without it: no count, no dots, and Remind me still registers.
      },
    )
    return () => {
      cancelled = true
    }
  }, [eventId, enabled, token, attempt])

  useEventChannel(enabled ? eventId : null, {
    scope: 'public',
    on: {
      'registrations.updated': (data) => {
        const next = data as Omit<RegistrationSummary, 'registered'>
        setSummary((current) => ({ count: next.count, places: next.places, registered: current?.registered ?? false }))
      },
    },
    onResync: () => setAttempt((n) => n + 1),
  })

  const register = useCallback(async () => {
    const res = await registrationsApi.register(eventId, browserTimeZone())
    setSummary(res.data)
  }, [eventId])
  const unregister = useCallback(async () => {
    const res = await registrationsApi.unregister(eventId)
    setSummary(res.data)
  }, [eventId])

  return { summary, register, unregister }
}
